import { posApi, saasApi } from '@/services/api';
import { useSyncStore } from '@/store/syncStore';
import { isNetworkError, pingServer } from './connectivity';
import {
  mapLocalToServer,
  resolvePayloadIds,
  resolveServerId,
} from './companyContext';
import { SYNC_CHUNK_SIZE, type SyncQueueItem, type SyncQueueType } from './db';
import { getPendingOrdered, refreshQueueCounts, updateQueueItem } from './queue';
import { companyDb } from './companyContext';

let drainInFlight = false;
let pauseRequested = false;

/** Upload data-URL images to Frappe before create/update item JSON. */
async function resolvePendingItemImage(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const img = String(payload.image || '');
  if (!img.startsWith('data:')) return payload;
  const blob = await (await fetch(img)).blob();
  const fd = new FormData();
  fd.append('file', new File([blob], `item-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' }));
  fd.append('is_private', '0');
  const res = await saasApi.uploadFile(fd);
  const fileUrl =
    (res.data?.message &&
      typeof res.data.message === 'object' &&
      (res.data.message as { file_url?: string }).file_url) ||
    (typeof res.data?.message === 'string' ? res.data.message : '');
  if (!fileUrl) throw new Error('Image upload returned no file_url');
  return { ...payload, image: fileUrl };
}

export function requestSyncPause() {
  pauseRequested = true;
  const progress = useSyncStore.getState().syncProgress;
  if (progress) {
    useSyncStore.getState().setSyncProgress({ ...progress, paused: true });
  }
}

export function clearSyncPause() {
  pauseRequested = false;
}

function extractServerName(res: { data?: { message?: unknown } }): string | undefined {
  const msg = res?.data?.message;
  if (!msg) return undefined;
  if (typeof msg === 'string') return msg;
  if (typeof msg === 'object' && msg !== null && 'name' in msg) {
    return String((msg as { name: string }).name);
  }
  return undefined;
}

/** Pull human message from Frappe / axios error bodies. */
export function parseSyncError(err: unknown): string {
  if (isNetworkError(err)) return 'Network error';
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (data?._server_messages) {
    try {
      const messages = JSON.parse(String(data._server_messages)) as string[];
      if (messages?.[0]) {
        const inner = JSON.parse(messages[0]) as { message?: string };
        if (inner?.message) return String(inner.message);
      }
    } catch {
      /* fall through */
    }
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  // OperationalError often puts the MySQL tuple only in `exc`
  if (typeof data?.exc === 'string' && data.exc) {
    const m = data.exc.match(/OperationalError:\s*\(([^)]+)\)/);
    if (m) return `Database error: (${m[1]})`;
    const line = data.exc.split('\n').find((l) => /OperationalError|Incorrect datetime|IntegrityError/i.test(l));
    if (line) return line.replace(/^[^\w]*/, '').slice(0, 240);
  }
  if (typeof data?.exc_type === 'string' && data.exc_type) {
    return `${data.exc_type}: sync failed`;
  }
  return (err as Error)?.message || 'Sync failed';
}

function normalizeSaleOfflineSource(payload: Record<string, unknown>): Record<string, unknown> {
  const invoice = { ...((payload.invoice || payload) as Record<string, unknown>) };
  const mode = useSyncStore.getState().posSyncMode;
  const allowed = new Set(['Offline Always Manual Sync', 'Local First Auto Sync']);
  if (!allowed.has(String(invoice.offline_source || ''))) {
    invoice.offline_source = allowed.has(mode) ? mode : 'Local First Auto Sync';
  }
  // MySQL Datetime rejects ISO / tz — send naive wall time.
  if (invoice.offline_sale_datetime) {
    const raw = String(invoice.offline_sale_datetime);
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      invoice.offline_sale_datetime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } else {
      invoice.offline_sale_datetime = raw
        .replace('T', ' ')
        .replace(/Z$/i, '')
        .replace(/[+-]\d{2}:\d{2}$/, '')
        .replace(/\.\d+/, '')
        .slice(0, 19);
    }
  }
  if (payload.invoice) return { ...payload, invoice };
  return { ...payload, ...invoice };
}

async function prepareWorkItem(item: SyncQueueItem): Promise<SyncQueueItem> {
  let payload = { ...item.payload };
  if (item.type === 'sale') {
    payload = normalizeSaleOfflineSource(payload);
  }
  const next: SyncQueueItem = {
    ...item,
    status: 'pending',
    error: undefined,
    payload,
  };
  await companyDb().sync_queue.put(next);
  return next;
}

async function rewriteLocalIdsInPayload(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  let out = await resolvePayloadIds({ ...payload }, [
    'item',
    'customer',
    'supplier',
    'variant',
    'pos_invoice',
    'cash_pocket',
    'bank_account',
  ]);

  // Drop ephemeral local names so create APIs insert instead of update
  if (typeof out.name === 'string' && out.name.startsWith('local_')) {
    delete out.name;
  }

  if (Array.isArray(out.items)) {
    out.items = await Promise.all(
      (out.items as Array<Record<string, unknown>>).map(async (line) => {
        const item = await resolveServerId(String(line.item || ''));
        const variant = line.variant ? await resolveServerId(String(line.variant)) : line.variant;
        return { ...line, item: item || line.item, variant };
      }),
    );
  }

  if (out.invoice && typeof out.invoice === 'object') {
    const inv = await rewriteLocalIdsInPayload(out.invoice as Record<string, unknown>);
    out = { ...out, invoice: inv };
  }

  return out;
}

async function postQueueItem(item: SyncQueueItem): Promise<string | undefined> {
  const payload = await rewriteLocalIdsInPayload({ ...item.payload });

  switch (item.type as SyncQueueType) {
    case 'create_item':
    case 'update_item': {
      const withImage = await resolvePendingItemImage(payload);
      Object.assign(payload, withImage);
      const res = await posApi.createItem(withImage);
      // Stash uploaded URL so afterSuccess can patch local catalog off data: URLs
      if (withImage.image && typeof withImage.image === 'string') {
        item.payload = { ...item.payload, image: withImage.image };
      }
      return extractServerName(res);
    }
    case 'create_customer':
    case 'update_customer': {
      const res = await posApi.createCustomer(payload);
      return extractServerName(res);
    }
    case 'create_supplier':
    case 'update_supplier': {
      const res = await posApi.createSupplier(payload);
      return extractServerName(res);
    }
    case 'stock_in': {
      const res = await posApi.createStockIn(payload);
      return extractServerName(res);
    }
    case 'stock_out': {
      const res = await posApi.createStockOut(payload);
      return extractServerName(res);
    }
    case 'stock_adjust': {
      const delta = Number(payload._adjust_delta) || 0;
      if (delta > 0) {
        const res = await posApi.createStockIn({
          ...payload,
          quantity: Math.abs(delta),
        });
        return extractServerName(res);
      }
      if (delta < 0) {
        const res = await posApi.createStockOut({
          ...payload,
          quantity: Math.abs(delta),
          stock_out: payload.reason || 'Adjustment',
        });
        return extractServerName(res);
      }
      return 'noop_adjust';
    }
    case 'sale': {
      const fixed = normalizeSaleOfflineSource(payload);
      Object.assign(payload, fixed);
      if (fixed.invoice) payload.invoice = fixed.invoice;
      const res = await posApi.createInvoice(payload);
      const name = extractServerName(res);
      if (!name) throw new Error('Server returned no invoice name');
      return name;
    }
    case 'purchase_invoice': {
      const res = await posApi.createPurchaseInvoice(payload);
      return extractServerName(res);
    }
    case 'expense': {
      const res = await posApi.createPOSExpense(payload);
      return extractServerName(res);
    }
    case 'sales_return': {
      // Resolve local invoice reference if sale synced later
      if (payload._local_invoice) {
        const localId = String(payload._local_invoice);
        const sale = await companyDb().local_sales.get(localId);
        if (sale?.serverInvoiceName) {
          payload.pos_invoice = sale.serverInvoiceName;
        } else {
          const byNo = await companyDb()
            .local_sales.filter((s) => s.offlineInvoiceNo === localId)
            .first();
          if (byNo?.serverInvoiceName) payload.pos_invoice = byNo.serverInvoiceName;
        }
      }
      const res = await posApi.createSalesReturn(payload as {
        pos_invoice: string;
        pos_shift?: string;
        items: { invoice_item: string; quantity: number }[];
        return_method?: string;
        return_bank?: string;
      });
      return extractServerName(res);
    }
    default:
      throw new Error(`Unknown queue type: ${item.type}`);
  }
}

async function afterSuccess(item: SyncQueueItem, serverName?: string) {
  if (!serverName || serverName === 'noop_adjust') {
    await updateQueueItem(item.clientOfflineId, { status: 'synced', serverName, error: undefined });
    return;
  }

  const localName =
    (typeof item.payload.name === 'string' && item.payload.name.startsWith('local_')
      ? item.payload.name
      : null) ||
    (item.clientOfflineId.startsWith('local_') ? item.clientOfflineId : null);

  if (localName && (item.type.startsWith('create_') || item.type.includes('item') || item.type.includes('customer') || item.type.includes('supplier'))) {
    await mapLocalToServer(localName, serverName);
    // Also map clientOfflineId variants
    await mapLocalToServer(item.clientOfflineId, serverName);
  }

  if (item.type === 'sale') {
    await companyDb().local_sales.update(item.clientOfflineId, { serverInvoiceName: serverName });
    await mapLocalToServer(item.clientOfflineId, serverName);
    const offlineNo = (item.payload.invoice as { offline_invoice_no?: string } | undefined)?.offline_invoice_no;
    if (offlineNo) await mapLocalToServer(offlineNo, serverName);
  } else if (item.type === 'expense') {
    await companyDb().local_expenses.update(item.clientOfflineId, { serverName });
  } else if (item.type === 'purchase_invoice') {
    await companyDb().local_purchases.update(item.clientOfflineId, { serverName });
  } else if (item.type === 'sales_return') {
    await companyDb().local_returns.update(item.clientOfflineId, { serverName });
  } else if (item.type.startsWith('stock_')) {
    await companyDb().local_stock_movements.update(item.clientOfflineId, { serverName });
  } else if (item.type === 'create_item') {
    const localItemName = String(item.payload.name || item.clientOfflineId);
    const existing = await companyDb().catalog_items.get(localItemName);
    const uploadedImage = String(item.payload.image || '');
    if (existing && localItemName !== serverName) {
      await companyDb().catalog_items.delete(localItemName);
      await companyDb().catalog_items.put({
        ...existing,
        name: serverName,
        isLocal: false,
        ...(uploadedImage && !uploadedImage.startsWith('data:') ? { image: uploadedImage } : {}),
      });
      const stock = await companyDb().local_stock.get(`${localItemName}|`);
      if (stock) {
        await companyDb().local_stock.delete(stock.id);
        await companyDb().local_stock.put({ ...stock, id: `${serverName}|`, item: serverName });
      }
    } else if (existing && uploadedImage && !uploadedImage.startsWith('data:')) {
      await companyDb().catalog_items.put({ ...existing, image: uploadedImage, isLocal: false });
    }
  } else if (item.type === 'update_item') {
    const name = String(serverName || item.payload.name || '');
    const uploadedImage = String(item.payload.image || '');
    if (name && uploadedImage && !uploadedImage.startsWith('data:')) {
      const existing = await companyDb().catalog_items.get(name);
      if (existing) {
        await companyDb().catalog_items.put({ ...existing, image: uploadedImage });
      }
    }
  } else if (item.type === 'create_customer') {
    const localName2 = String(item.payload.name || item.clientOfflineId);
    const existing = await companyDb().catalog_customers.get(localName2);
    if (existing && localName2 !== serverName) {
      await companyDb().catalog_customers.delete(localName2);
      await companyDb().catalog_customers.put({ ...existing, name: serverName, isLocal: false });
    }
  } else if (item.type === 'create_supplier') {
    const localName2 = String(item.payload.name || item.clientOfflineId);
    const existing = await companyDb().catalog_suppliers.get(localName2);
    if (existing && localName2 !== serverName) {
      await companyDb().catalog_suppliers.delete(localName2);
      await companyDb().catalog_suppliers.put({ ...existing, name: serverName, isLocal: false });
    }
  }

  await updateQueueItem(item.clientOfflineId, { status: 'synced', serverName, error: undefined });
}

export async function drainSyncQueue(opts?: { force?: boolean }): Promise<{ done: number; failed: number }> {
  const store = useSyncStore.getState();
  if (!opts?.force && store.posSyncMode === 'Offline Always Manual Sync') {
    // Manual mode: only Sync Now (force) drains
    return { done: 0, failed: 0 };
  }
  if (store.posSyncMode === 'Live Online') return { done: 0, failed: 0 };
  if (drainInFlight) return { done: 0, failed: 0 };

  const reachable = store.isServerReachable || (await pingServer());
  if (!reachable) {
    store.setLastSyncError('Server unreachable');
    return { done: 0, failed: 0 };
  }

  drainInFlight = true;
  pauseRequested = false;
  store.setIsSyncing(true);
  store.setLastSyncError(null);

  let done = 0;
  let failed = 0;

  try {
    // Snapshot once — do not re-include items that fail in this pass (avoids infinite retry).
    const snapshot = await getPendingOrdered();
    const work: SyncQueueItem[] = [];
    for (const item of snapshot) {
      work.push(await prepareWorkItem(item));
    }
    await refreshQueueCounts();

    const total = work.length;
    store.setSyncProgress({ total, done, failed, paused: false });

    for (let i = 0; i < work.length; i += SYNC_CHUNK_SIZE) {
      if (pauseRequested) {
        store.setSyncProgress({ total, done, failed, paused: true });
        break;
      }

      const chunk = work.slice(i, i + SYNC_CHUNK_SIZE);
      for (const item of chunk) {
        if (pauseRequested) break;
        if (!(await pingServer())) {
          store.setLastSyncError('Connection lost during sync');
          store.setSyncProgress({ total, done, failed, paused: true });
          pauseRequested = true;
          break;
        }

        await updateQueueItem(item.clientOfflineId, { status: 'syncing' });
        try {
          const serverName = await postQueueItem(item);
          await afterSuccess(item, serverName);
          done += 1;
        } catch (err) {
          const message = parseSyncError(err);
          if (isNetworkError(err)) {
            await updateQueueItem(item.clientOfflineId, { status: 'pending', error: message });
            store.setLastSyncError(message);
            pauseRequested = true;
            break;
          }
          failed += 1;
          await updateQueueItem(item.clientOfflineId, {
            status: 'failed',
            error: message,
            retryCount: (item.retryCount || 0) + 1,
          });
          store.setLastSyncError(message);
        }
        store.setSyncProgress({ total, done, failed, paused: false });
      }

      if (pauseRequested) break;
      await new Promise((r) => setTimeout(r, 300));
    }
  } finally {
    drainInFlight = false;
    store.setIsSyncing(false);
    await refreshQueueCounts();
    const progress = store.syncProgress;
    if (progress && !pauseRequested) {
      store.setSyncProgress(null);
    }
  }

  return { done, failed };
}

/** Mode 2 auto-drain; Mode 1 no-op unless force. */
export async function maybeAutoSync(): Promise<void> {
  const mode = useSyncStore.getState().posSyncMode;
  if (mode === 'Local First Auto Sync') {
    void drainSyncQueue({ force: false });
  }
}

export async function syncNow(): Promise<{ done: number; failed: number }> {
  clearSyncPause();
  return drainSyncQueue({ force: true });
}
